import {
  Box,
  Button,
  Container,
  Flex,
  PasswordInput,
  Select,
  SimpleGrid,
  Text,
  TextInput,
} from '@mantine/core'
import { UseFormReturnType } from '@mantine/form'
import { IconEyeCheck, IconEyeOff } from '@tabler/icons-react'
import ActionMenu from '@v2/commons/components/ActionMenu/ActionMenu.component'
import PhoneNumberSelect from '@v2/commons/components/PhoneNumberSelect/PhoneNumberSelect.component'

import { Icons } from 'src/common/components'
import withErrorBoundaryAndPolicyCheck from 'src/v2/commons/HOC/ErrorBoundaryAndPolicyCheckHOC'

import { ICreateUserRequest, USER_TYPE_OPTIONS } from '../../user.types'
import { openUserDeleteModal } from '../DeleteUserModal/DeleteUserModal.component'
import classes from './UserInformation.module.scss'

interface UserInformationProps {
  onSubmit: () => void
  editForm: boolean
  setEditForm: (values: boolean) => void
  handleCancelForm: () => void
  userInformationForm: UseFormReturnType<ICreateUserRequest>
  loading: boolean
  isCreateNew?: boolean
}

const UserInformation = ({
  onSubmit,
  editForm,
  setEditForm,
  handleCancelForm,
  userInformationForm,
  loading,
  isCreateNew = false,
}: UserInformationProps) => {
  const VisibilityToggleIcon = ({ reveal }: { reveal: boolean }) =>
    editForm ? (
      reveal ? (
        <IconEyeOff
          style={{
            width: 'var(--psi-icon-size)',
            height: 'var(--psi-icon-size)',
          }}
        />
      ) : (
        <IconEyeCheck
          style={{
            width: 'var(--psi-icon-size)',
            height: 'var(--psi-icon-size)',
          }}
        />
      )
    ) : null

  return (
    <Box my="md">
      <Flex
        justify="space-between"
        align="flex-start"
        className={classes.headerWrapper}
      >
        <Text fw={700} c="blue.5">
          User Information
        </Text>
        {!isCreateNew && (
          <ActionMenu
            menuItems={[
              {
                label: 'Update',
                color: 'blue',
                icon: <Icons.Edit />,
                onClick: () => setEditForm(true),
                enable: true,
              },
              {
                label: 'Delete',
                color: 'red',
                icon: <Icons.Trash />,
                onClick: () =>
                  openUserDeleteModal(
                    userInformationForm.getValues()?.id ?? '',
                  ),
                enable: true,
              },
            ]}
          />
        )}
      </Flex>
      <Flex justify="center">
        <Container mx="md" className={classes.flex1} my="lg">
          <form
            onSubmit={(e) => {
              e.preventDefault()
              onSubmit()
            }}
          >
            <SimpleGrid cols={2} spacing="xl" verticalSpacing="xl">
              <TextInput
                key={userInformationForm.key('username')}
                label="Username (Email)"
                withAsterisk
                readOnly={!editForm}
                {...userInformationForm.getInputProps('username')}
              />
              <TextInput
                key={userInformationForm.key('firstName')}
                label="First Name"
                withAsterisk
                readOnly={!editForm}
                {...userInformationForm.getInputProps('firstName')}
              />
              <TextInput
                key={userInformationForm.key('lastName')}
                withAsterisk
                readOnly={!editForm}
                label="Last Name"
                {...userInformationForm.getInputProps('lastName')}
              />
              <PhoneNumberSelect
                readOnly={!editForm}
                initialCountryCode="US"
                {...userInformationForm.getInputProps('phone')}
                key={userInformationForm.key('phone')}
                initialValue={userInformationForm.getValues()['phone']}
              />
              <PasswordInput
                key={userInformationForm.key('password')}
                readOnly={!editForm}
                label="Password"
                {...userInformationForm.getInputProps('password')}
                visibilityToggleIcon={VisibilityToggleIcon}
              />
              <TextInput
                key={userInformationForm.key('aircallUserId')}
                readOnly={!editForm}
                label="Aircall User ID"
                {...userInformationForm.getInputProps('aircallUserId')}
              />
              {/* Commented out because HIPAA flag not working on BE */}
              {/* <Checkbox
                key={userInformationForm.key('hipaaCertified')}
                readOnly={!editForm}
                label="HIPAA Certified"
                {...userInformationForm.getInputProps('hipaaCertified')}
              /> */}
              <Select
                key={userInformationForm.key('type')}
                label="User Type"
                withAsterisk
                readOnly={!editForm}
                {...userInformationForm.getInputProps('type')}
                placeholder="Select User Type"
                data={USER_TYPE_OPTIONS}
                allowDeselect={false}
              />
            </SimpleGrid>
            {editForm && (
              <Flex justify="space-between" align="flex-start" mt="lg">
                <Button onClick={() => handleCancelForm()} variant="outline">
                  Cancel
                </Button>
                <Button loading={loading} type="submit" variant="filled">
                  Save
                </Button>
              </Flex>
            )}
          </form>
        </Container>
      </Flex>
    </Box>
  )
}

export default withErrorBoundaryAndPolicyCheck(
  UserInformation,
  'is_enable_leads_table',
  true,
)
